/**
 * Where focus goes when the re-read after an e-laskuehdotus attempt changes the
 * view (048). Split out of `EInvoiceProposalRequest.tsx` to keep it under the
 * web 400-line rule; the component still owns the refs and the state.
 *
 * Owned by feature 048-member-einvoice-request.
 */

import { type RefObject, useEffect } from 'react';

import { focusIsUnclaimed } from './einvoiceProposalHelpers.js';
import type { EligibilityKind } from './einvoiceSubmission.js';

export interface ReReadFocusOptions {
  readonly eligibilityKind: EligibilityKind;
  /** `isSupersededBy(submission, eligibilityKind)` — the outcome message is now empty. */
  readonly superseded: boolean;
  readonly liveMessage: string;
  readonly pickerOpen: boolean;
  readonly closePicker: () => void;
  readonly refs: {
    readonly status: RefObject<HTMLElement | null>;
    readonly pending: RefObject<HTMLElement | null>;
    readonly unavailable: RefObject<HTMLElement | null>;
    /** The caller's delivery-method value — the statement for `already-einvoice`/`method-unknown`. */
    readonly method: RefObject<HTMLElement | null>;
  };
}

export function useReReadFocus({
  eligibilityKind,
  superseded,
  liveMessage,
  pickerOpen,
  closePicker,
  refs,
}: ReReadFocusOptions): void {
  // The element that states the re-read state, for focus to land on when the
  // outcome message empties: the pending block, the unavailable note, or — for
  // `already-einvoice`/`method-unknown`, whose statement is the delivery-method
  // row in the caller's card — that row's value (never an empty live region).
  const home =
    eligibilityKind === 'pending'
      ? refs.pending
      : eligibilityKind === 'unavailable'
        ? refs.unavailable
        : eligibilityKind === 'can-request'
          ? refs.status
          : refs.method;

  // "The outcome has a home and focus lands on it": once the re-read takes over
  // it is that home, not the region that just emptied — unless the member has
  // since moved focus elsewhere, which an async re-read must not steal.
  useEffect(() => {
    if (!superseded || !focusIsUnclaimed(refs.status.current)) return;
    (home.current ?? refs.status.current)?.focus();
  }, [superseded, home, refs.status]);

  // A re-read can leave `can-request` with the picker open (`unsupported-bank`
  // keeps it open, focus on Submit): that branch never renders the picker, so
  // close it and give focus a home that says something rather than <body>.
  const leftCanRequest = eligibilityKind !== 'can-request';
  useEffect(() => {
    if (!leftCanRequest || !pickerOpen) return;
    closePicker();
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    ((liveMessage === '' ? home.current : null) ?? refs.status.current)?.focus();
  }, [leftCanRequest, pickerOpen, closePicker, liveMessage, home, refs.status]);
}
